import { FormatBold, FormatItalic, FormatUnderlined, StrikethroughS, FormatColorFill, FormatColorText } from "@mui/icons-material";
import {Box, ToggleButtonGroup, ToggleButton, TextField, MenuItem} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import {useMemo, useState} from "react";
import ColorInput from "../../../utility/ColorInput";
import {getInitialStyle, StyleOptions, StyleTargets} from "../../../utility/Style";
import DefaultStyles from "./DefaultStyles";

const useStyles = makeStyles((theme) => ({
    content: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        padding: theme.spacing(1),
    },
    colors: {
        display: "flex",
        gap: theme.spacing(1)
    }
}));

interface Props
{
    formatStyle: Partial<StyleOptions>
    setFormatStyle: (style: StyleOptions) => void
}

export function FormattingStyleOptions({ formatStyle, setFormatStyle }: Props)
{
    const classes = useStyles();
    const [target, setTarget] = useState<StyleTargets | "all">("all");
    const fullStyle = useMemo(() => ({ ...getInitialStyle(), ...formatStyle }), [formatStyle]);
    const formats = useMemo(() =>
    {
        const formats: string[] = [];

        if (target === "all") {
            if (fullStyle.bold)
                formats.push("bold");
            if (fullStyle.italic)
                formats.push("italic");
            if (fullStyle.underline)
                formats.push("underline");
            if (fullStyle.strike)
                formats.push("strike");
        } else {
            if (fullStyle[target]?.bold)
                formats.push("bold");
            if (fullStyle[target]?.italic)
                formats.push("italic");
            if (fullStyle[target]?.underline)
                formats.push("underline");
            if (fullStyle[target]?.strike)
                formats.push("strike");
        }
        return formats;
    }, [fullStyle, target]);
    const getColor = (color: "backColor" | "textColor"): string | null => {
        if (target === "all") {
            return fullStyle[color] || null;
        }
        return fullStyle[target]![color];
    }

    const handleFormat = (event: React.MouseEvent<HTMLElement>, formats: string[]) =>
    {
        if (target === "all") {
            setFormatStyle({
                ...fullStyle,
                bold: formats.includes("bold"),
                italic: formats.includes("italic"),
                underline: formats.includes("underline"),
                strike: formats.includes("strike"),
            });
        } else {
            setFormatStyle({
                ...fullStyle,
                [target]: {
                    bold: formats.includes("bold"),
                    italic: formats.includes("italic"),
                    underline: formats.includes("underline"),
                    strike: formats.includes("strike"),
                }
            });
        }
    };

    const setTextColor = (color: string | null) =>
    {
        if (target === "all") {
            setFormatStyle({
                ...fullStyle,
                textColor: color
            });
        } else {
            setFormatStyle({
                ...fullStyle,
                [target]: {
                    textColor: color
                }
            });
        }
    }

    const setBackColor = (color: string | null) =>
    {
        if (target === "all") {
            setFormatStyle({
                ...fullStyle,
                backColor: color
            });
        } else {
            setFormatStyle({
                ...fullStyle,
                [target]: {
                    backColor: color
                }
            });
        }
    }

    const setColors = (textColor: string | null, backColor: string | null) =>
    {
        if (target === "all") {
            setFormatStyle({
                ...fullStyle,
                backColor,
                textColor
            });
        } else {
            setFormatStyle({
                ...fullStyle,
                [target]: {
                    backColor,
                    textColor
                }
            });
        }
    }

    return (
        <Box className={classes.content}>
            <TextField onChange={({target}) =>
                setTarget(target.value as StyleTargets)} select size="small" sx={{flexGrow: 1}} value={target}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="header">Header</MenuItem>
                <MenuItem value="body">Body</MenuItem>
                <MenuItem value="footer">Footer</MenuItem>
            </TextField>
            <ToggleButtonGroup value={formats} onChange={handleFormat}>
                <ToggleButton value="bold" aria-label="bold" size={"small"}>
                    <FormatBold />
                </ToggleButton>
                <ToggleButton value="italic" aria-label="italic" size={"small"}>
                    <FormatItalic />
                </ToggleButton>
                <ToggleButton value="underline" aria-label="underline" size={"small"}>
                    <FormatUnderlined />
                </ToggleButton>
                <ToggleButton value="strike" aria-label="strike" size={"small"}>
                    <StrikethroughS />
                </ToggleButton>
            </ToggleButtonGroup>
            <Box className={classes.colors}>
                <ColorInput value={getColor("backColor")} onChange={setBackColor} icon={<FormatColorFill />} />
                <ColorInput value={getColor("textColor")} onChange={setTextColor} icon={<FormatColorText />} />
                <DefaultStyles onSelect={setColors} />
            </Box>
        </Box>
    );
}